/**
 * „Diese Rolle wurde um 14:32 von … geändert." — der Hinweis über einem
 * offenen Editor, wenn jemand anders am selben Objekt geschrieben hat.
 *
 * Gewünscht im Termin mit der HOWOGE am 07.10.2026 (Punkt 19), mit dem Namen
 * dessen, der geändert hat. Zwei Wege hinaus:
 *
 * - **Neu laden** holt den jetzigen Stand und verwirft die eigenen Eingaben.
 * - **Weiterarbeiten** blendet den Hinweis aus. Beim Speichern prüft die
 *   Schnittstelle weiterhin die Version; eine fremde Änderung wird nicht
 *   still überschrieben, sondern führt in den Konfliktweg.
 *
 * Höflich angesagt (`role="status"`), nicht unterbrechend: der Hinweis
 * kündigt eine Lage an, er verlangt keine sofortige Antwort.
 */

import { stil } from './stil';
import { uhrzeit, type LiveEintrag } from '../api/live';

/** Welche Felder geändert wurden — als Wörter, nicht als Spaltennamen. */
function feldText(e: LiveEintrag): string {
  if (e.objektart === 'Zuordnung') return 'Rollenzuordnung';
  const felder = (e.felder ?? []).filter(Boolean);
  if (felder.length === 0) return '';
  if (felder.length > 4) return `${felder.length} Felder`;
  return felder.join(', ');
}

export function Livehinweis({
  eintrag, sache, ungespeichert, onNeuLaden, onWeiter, verwirft = 'Ihre Eingaben', mitFeldern = true,
}: {
  eintrag: LiveEintrag;
  /** „Diese Rolle", „Dieses Asset". */
  sache: string;
  /** Was „Neu laden" verwirft — „Ihre Eingaben", „Ihre Rollenauswahl". */
  verwirft?: string;
  /** Die geänderten Felder nennen? Bei der Rollenzuordnung sagt das der Satz selbst. */
  mitFeldern?: boolean;
  /** Gibt es Eingaben, die „Neu laden" verwerfen würde? */
  ungespeichert: boolean;
  onNeuLaden: () => void;
  onWeiter: () => void;
}) {
  const wer = eintrag.akteurName?.trim() || 'jemand anderem';
  const was = mitFeldern ? feldText(eintrag) : '';
  return (
    <div role="status" aria-live="polite" style={stil(
      'display:flex;align-items:flex-start;gap:11px;flex-wrap:wrap;padding:12px 14px;'
      + 'background:var(--info-subtle);border:1px solid var(--border-subtle);'
      + 'box-shadow:inset 3px 0 0 var(--info);border-radius:var(--r-md);margin-bottom:16px',
    )}>
      <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none"
           stroke="var(--info)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
           style={stil('flex:0 0 17px;margin-top:2px')}>
        <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />
      </svg>
      <div style={stil('flex:1 1 240px;min-width:0;font-size:13px;line-height:20px;color:var(--text-primary)')}>
        <strong style={stil('font-weight:600')}>
          {sache} wurde um {uhrzeit(eintrag.zeitpunkt)} von {wer} geändert.
        </strong>
        {was && <span style={stil('color:var(--text-secondary)')}> Geändert: {was}.</span>}
        <div style={stil('color:var(--text-secondary)')}>
          {ungespeichert
            ? `Neu laden zeigt den jetzigen Stand und verwirft ${verwirft}.`
            : 'Neu laden zeigt den jetzigen Stand.'}
        </div>
      </div>
      <div style={stil('display:flex;gap:8px;flex-wrap:wrap;align-items:center')}>
        <button type="button" className="knopf knopf--rand" onClick={onNeuLaden}>Neu laden</button>
        <button type="button" className="knopf" onClick={onWeiter}>Weiterarbeiten</button>
      </div>
    </div>
  );
}
